/**
 * DOCX exporter using server-side pandoc conversion with template support
 */

import { BaseExporter } from './BaseExporter.js';
import type { ExportFormat, ExportOptions, ExportResult } from './types.js';

export class DocxExporter extends BaseExporter {
  private getAuthToken?: () => string | null;

  constructor(getAuthToken?: () => string | null) {
    super();
    this.getAuthToken = getAuthToken;
  }

  getFormat(): ExportFormat {
    return 'docx';
  }

  async export(markdown: string, options?: ExportOptions): Promise<ExportResult> {
    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };

      // Add auth token if available
      const token = this.getAuthToken?.();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch('/api/export/docx', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          markdown,
          title: options?.title || options?.filename || 'document',
          templateId: options?.templateId ? Number(options.templateId) : undefined,
          projectId: options?.projectId ? Number(options.projectId) : undefined,
          options: {
            includeToc: options?.docxOptions?.includeToc ?? false,
            includeFrontPage: options?.docxOptions?.includeFrontPage ?? false,
            subtitle: options?.docxOptions?.subtitle || '',
            company: options?.docxOptions?.company || '',
          },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || `Server error: ${response.statusText}`;

        if (response.status === 503) {
          throw new Error(`Export requires pandoc on the server. Please install pandoc or check server configuration.`);
        }

        throw new Error(errorMessage);
      }

      const blob = await response.blob();
      return {
        success: true,
        blob,
        filename: this.getFilename(options),
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Export failed',
      };
    }
  }
}
